import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { intakeQuestionnaireActions } from '../../../../store';
import {
  hasFamilyFieldErrors,
  hasFamilyFieldValue,
  isFamilyComplete,
  mapEducationInfoToValues,
} from '../../../common';
import { INTAKE_TAB_IDS } from '../constants';
import { GENERAL_INFO_FORM_SCHEMA } from '../components/forms/general/validationSchema';
import { getIntakeGeneralInfoInitialValues } from '../components/forms/general/utils';
import { SCREENING_DATES_REQUIRED_STATE } from '../components/forms/medical/constants';
import { getMedicalInfoSchema } from '../components/forms/medical/validationSchema';
import { getMedicalInfoInitialValues } from '../components/forms/medical/hooks/utils';
import { areIntakeMedicalDiagnosesComplete } from '../components/forms/medical/utils';
import { EDUCATION_INFO_SCHEMA } from '../components/forms/education/validationSchema';
import { areIntakeMedicationsComplete } from '../components/forms/medication/utils';
import { areIntakeReferralsComplete } from '../components/forms/referral/utils';
import { areIntakeTherapiesComplete } from '../components/forms/therapy/utils';
import { initializeTabStatuses } from '../../common/hooks';
import useInitializeIntakeChildTabStatus from './useInitializeIntakeChildTabStatus';

import type { AppDispatch, RootState } from '../../../../store';
import type {
  IIntakeMedicalInfo,
  IIntakeReferral,
} from '../../../../store/questionnaires/intake';
import type { IMedication, ITherapy } from '../../../common';
import type { QuestionnaireTabStatusEntry } from '../../common/hooks';
import type { IntakeTabId } from '../types';

export default function useInitializeIntakeTabStatuses() {
  const dispatch = useDispatch<AppDispatch>();
  const {
    generalInfo,
    medications,
    hasNoMedications,
    referrals,
    therapies,
    hasNoTherapies,
    medicalInfo,
    educationInfo,
    family,
    notAttendingSchool,
  } = useIntakeTabStatusDetails();
  useInitializeIntakeChildTabStatus();

  useEffect(() => {
    const tabEntries: QuestionnaireTabStatusEntry<IntakeTabId>[] = [
      {
        tabId: INTAKE_TAB_IDS.GENERAL,
        values: getIntakeGeneralInfoInitialValues(generalInfo),
        schema: GENERAL_INFO_FORM_SCHEMA,
      },
      getIntakeMedicalTabEntry(medicalInfo, generalInfo?.client_street_address_state),
    ];

    // Marking the child as not attending school completes the education tab on its own;
    // otherwise the tab tracks the education info scalar form like general/medical.
    if (!notAttendingSchool) {
      tabEntries.push({
        tabId: INTAKE_TAB_IDS.EDUCATION,
        values: mapEducationInfoToValues(educationInfo),
        schema: EDUCATION_INFO_SCHEMA,
      });
    }

    initializeTabStatuses(tabEntries, {
      updateTabStatus: intakeQuestionnaireActions.updateIntakeTabStatus,
      dispatch,
    });

    const listStatuses = getIntakeListTabStatuses({
      medications,
      referrals,
      therapies,
      hasNoMedications,
      hasNoTherapies,
    });
    listStatuses.forEach(({ tabId, hasSavedValues, success }) => {
      dispatch(
        intakeQuestionnaireActions.updateIntakeTabStatus({
          tabId,
          status: { error: hasSavedValues && !success, success },
        }),
      );
    });
    if (notAttendingSchool) {
      dispatch(
        intakeQuestionnaireActions.updateIntakeTabStatus({
          tabId: INTAKE_TAB_IDS.EDUCATION,
          status: { error: false, success: true },
        }),
      );
    }
    dispatch(
      intakeQuestionnaireActions.updateIntakeTabStatus({
        tabId: INTAKE_TAB_IDS.FAMILY,
        status: {
          error: hasFamilyFieldValue(family) && hasFamilyFieldErrors(family),
          success: isFamilyComplete(family),
        },
      }),
    );
  }, [
    dispatch,
    educationInfo,
    family,
    generalInfo,
    hasNoMedications,
    hasNoTherapies,
    medicalInfo,
    medications,
    notAttendingSchool,
    referrals,
    therapies,
  ]);
}

function useIntakeTabStatusDetails() {
  const generalInfo = useSelector(
    (state: RootState) => state.intakeQuestionnaire.intakeQuestionnaireDetails?.general_info,
  );
  const medications = useSelector(
    (state: RootState) => state.intakeQuestionnaire.intakeQuestionnaireDetails?.medications,
  );
  const hasNoMedications = useSelector(
    (state: RootState) =>
      state.intakeQuestionnaire.intakeQuestionnaireDetails?.has_no_medications ?? false,
  );
  const referrals = useSelector(
    (state: RootState) => state.intakeQuestionnaire.intakeQuestionnaireDetails?.referrals,
  );
  const therapies = useSelector(
    (state: RootState) => state.intakeQuestionnaire.intakeQuestionnaireDetails?.therapies,
  );
  const hasNoTherapies = useSelector(
    (state: RootState) =>
      state.intakeQuestionnaire.intakeQuestionnaireDetails?.has_no_therapies ?? false,
  );
  const medicalInfo = useSelector(
    (state: RootState) => state.intakeQuestionnaire.intakeQuestionnaireDetails?.medical_info,
  );
  const educationInfo = useSelector(
    (state: RootState) => state.intakeQuestionnaire.intakeQuestionnaireDetails?.education_info,
  );
  const family = useSelector(
    (state: RootState) => state.intakeQuestionnaire.intakeQuestionnaireDetails?.family,
  );
  const notAttendingSchool = useSelector(
    (state: RootState) =>
      state.intakeQuestionnaire.intakeQuestionnaireDetails?.not_attending_school ?? false,
  );
  return {
    generalInfo,
    medications,
    hasNoMedications,
    referrals,
    therapies,
    hasNoTherapies,
    medicalInfo,
    educationInfo,
    family,
    notAttendingSchool,
  };
}

function getIntakeMedicalTabEntry(
  medicalInfo: IIntakeMedicalInfo | null | undefined,
  clientState: string | null | undefined,
): QuestionnaireTabStatusEntry<IntakeTabId> {
  return {
    tabId: INTAKE_TAB_IDS.MEDICAL,
    values: getMedicalInfoInitialValues(medicalInfo),
    schema: getMedicalInfoSchema(clientState === SCREENING_DATES_REQUIRED_STATE),
    extraRequirementMet:
      (medicalInfo?.early_interventions?.length ?? 0) > 0 &&
      (medicalInfo?.pregnancy_issues?.length ?? 0) > 0 &&
      (medicalInfo?.regressions?.length ?? 0) > 0 &&
      areIntakeMedicalDiagnosesComplete(medicalInfo?.diagnoses ?? []),
    hasNonFormikSavedValues:
      (medicalInfo?.early_interventions?.length ?? 0) > 0 ||
      (medicalInfo?.pregnancy_issues?.length ?? 0) > 0 ||
      (medicalInfo?.regressions?.length ?? 0) > 0 ||
      (medicalInfo?.diagnoses?.length ?? 0) > 0,
  };
}

// `hasSavedValues` mirrors the scalar tabs' per-tab gate: a list tab has saved data once it has at
// least one row, or the "no medications" / "no therapies" opt-out has been recorded. It drives the
// error icon so an untouched list tab stays neutral even after the questionnaire is started elsewhere.
function getIntakeListTabStatuses({
  medications,
  referrals,
  therapies,
  hasNoMedications,
  hasNoTherapies,
}: {
  medications: IMedication[] | null | undefined;
  referrals: IIntakeReferral[] | null | undefined;
  therapies: ITherapy[] | null | undefined;
  hasNoMedications: boolean;
  hasNoTherapies: boolean;
}): Array<{ tabId: IntakeTabId; hasSavedValues: boolean; success: boolean }> {
  const medicationRows = medications ?? [];
  const referralRows = referrals ?? [];
  const therapyRows = therapies ?? [];
  return [
    {
      tabId: INTAKE_TAB_IDS.MEDICATION,
      hasSavedValues: hasNoMedications || medicationRows.length > 0,
      success:
        hasNoMedications ||
        (medicationRows.length > 0 && areIntakeMedicationsComplete(medicationRows)),
    },
    {
      tabId: INTAKE_TAB_IDS.REFERRAL,
      hasSavedValues: referralRows.length > 0,
      success: referralRows.length > 0 && areIntakeReferralsComplete(referralRows),
    },
    {
      tabId: INTAKE_TAB_IDS.THERAPY,
      hasSavedValues: hasNoTherapies || therapyRows.length > 0,
      success:
        hasNoTherapies || (therapyRows.length > 0 && areIntakeTherapiesComplete(therapyRows)),
    },
  ];
}
